/**
 * 마케팅 동의 셀프 철회 — 노출 판정 + 호출 로직 단일 소유자 (admin#2472 / #884).
 *
 * 철회 링크는 대기중/호출됨/착석/픽업 준비 등 토큰이 살아 있는 모든 화면 하단에
 * 동일하게 뜬다. 조건 계산과 API 호출을 화면마다 두면 6곳에서 어긋나므로 이 훅
 * 하나가 소유하고, 화면 조각(WaitlistMarketingConsentWithdraw)은 표시만 한다.
 *
 * ## 노출 판정: 서버 상태 ∪ 로컬 플래그, 단 서버의 false 는 플래그를 정리한다
 * 동의는 등록 1건이 아니라 연락처 단위로 산다(admin#2491 §11.8). 상태 응답의
 * `marketing_consent.current`(admin#2493)가 "이 연락처가 지금 수신 대상인가"를
 * 알려주므로, 재방문·기기 교체로 로컬 플래그를 잃은 고객에게도 링크가 뜬다.
 * 필드가 없는 구서버에서는 매장별 localStorage 플래그로 폴백한다.
 *
 * 신서버가 `current: false` 를 주면 그것이 권위다 — WhatsApp `BAJA` 나 고객센터
 * 대행으로 이미 전역 억제가 선 고객에게 남은 단말 플래그가 "철회하세요"를 계속
 * 권하지 않도록, 그 매장 플래그를 정리한다(합집합이 영원히 참으로 굳지 않게).
 *
 * `marketing_consent` 에 곧 `store_followed` 가 붙지만(follow 설계 §3.1) 이 훅이
 * 보는 것은 **`current` 하나뿐**이다 — 팔로우는 매장 단위 관계, 철회는 연락처 전역
 * 통제권이다. 그래서 불리언을 풀어 받지 않고 객체를 통째로 받는다.
 *
 * ## 404 중 무엇을 완료로 보는가
 * `error_code: 'CONSENT_NOT_FOUND'` 인 404 만 완료다 — 서버가 "닫을 동의 증거가
 * 없다"고 판단한 응답이라 재시도해도 결과가 같다. 반면 error_code 없는 404(라우트
 * 바인딩이 토큰을 못 찾음)는 **아무 것도 철회되지 않은** 상태이므로 일반 실패로
 * 보낸다(판정은 {@link isWaitlistConsentAbsentError}).
 *
 * ## 409 를 완료로 보지 않는 이유
 * `CONSENT_WITHDRAWAL_UNAVAILABLE`(admin#2493)은 **철회가 일어나지 않은** 상태다.
 * 완료로 삼키면 고객은 수신이 끊겼다고 믿지만 실제로는 계속 받는다 — 개인정보
 * 통제권에 대한 거짓 안내다. 완료도 일반 오류도 아닌 전용 안내로 갈라, 사람이
 * 처리할 수 있는 다른 경로(이메일·WhatsApp)를 알려준다.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  isWaitlistConsentAbsentError,
  isWaitlistConsentWithdrawalUnavailableError,
  waitlistApi,
} from '@/lib/api/waitlist';
import type { WaitlistPartyMarketingConsentState } from '@/lib/api/waitlist';
import {
  clearAllWaitlistMarketingConsents,
  clearWaitlistMarketingConsent,
  readWaitlistMarketingConsent,
  readWaitlistMarketingWithdrawn,
  saveWaitlistMarketingWithdrawn,
} from '@/components/waitlist/storage';
import { logger } from '@/utils/logger';

/**
 * 진행 상태. 완료(done)는 이 열거에 없다 — 완료는 "어느 토큰으로 철회했는가"
 * ({@link withdrawnToken})로 기억해야 폴링으로 상태가 다시 그려져도 유지된다.
 */
type WithdrawalPhase = 'idle' | 'pending' | 'failed' | 'unavailable';

export interface UseMarketingConsentWithdrawalResult {
  /** 철회 링크(또는 완료 문구)를 렌더할지. */
  visible: boolean;
  withdrawing: boolean;
  withdrawn: boolean;
  /** 재시도로 풀릴 수 있는 일반 실패 — 일반 오류 문구. */
  failed: boolean;
  /** 409 `CONSENT_WITHDRAWAL_UNAVAILABLE` — 완료가 아니며 전용 안내 문구. */
  unavailable: boolean;
  withdraw: () => Promise<void>;
}

/**
 * @param storeId  매장별 플래그 키. 매장 해석 전에는 null.
 * @param accessToken 철회 자격증명. 없으면 호출 자체가 불가하다.
 * @param enabled  토큰이 유효한 상태 화면인가(미등록 화면에서는 false).
 * @param consent  상태 응답의 `marketing_consent` 객체 그대로(admin#2493).
 *        구서버는 undefined — 그때만 로컬 플래그로 폴백하고, 전역 정리도 삼간다.
 */
export function useMarketingConsentWithdrawal(
  storeId: number | null,
  accessToken: string | null,
  enabled: boolean,
  consent?: WaitlistPartyMarketingConsentState,
): UseMarketingConsentWithdrawalResult {
  /** 서버가 이 연락처의 현재 수신 여부를 알려주는가 — 구서버는 undefined. */
  const serverConsentCurrent = consent?.current;
  const [consented, setConsented] = useState(false);
  const [phase, setPhase] = useState<WithdrawalPhase>('idle');
  /**
   * 철회를 마친 access_token. 폴링이 상태를 다시 그려도, 서버의 `current` 가 아직
   * 갱신 전이라 true 로 남아 있어도 이 토큰에 대해서는 링크를 되살리지 않는다 —
   * 방금 철회한 고객에게 "철회하세요"를 다시 권하지 않기 위함이다.
   */
  const [withdrawnToken, setWithdrawnToken] = useState<string | null>(null);
  /**
   * 직전 세션에서 이 토큰으로 철회를 마쳤는가(단말 기록, 짧은 TTL). 메모리 state 만
   * 있으면 새로고침 한 번에 완료 문구가 사라지고, 서버 `current` 가 아직 갱신 전이면
   * 링크가 되살아난다 — 방금 끝낸 일을 다시 권하지 않도록 storage 에서 복원한다.
   */
  const [recentlyWithdrawn, setRecentlyWithdrawn] = useState(false);

  // 다른 매장/다른 등록으로 바뀌면 이전 등록의 철회 결과를 그대로 물려받지 않는다.
  useEffect(() => {
    if (storeId === null || !enabled) {
      setConsented(false);
      setRecentlyWithdrawn(false);
      return;
    }
    setConsented(readWaitlistMarketingConsent(storeId));
    setRecentlyWithdrawn(accessToken !== null && readWaitlistMarketingWithdrawn(accessToken));
    setPhase('idle');
  }, [storeId, accessToken, enabled]);

  /**
   * 서버의 권위 있는 false 와 단말 플래그를 조정한다.
   *
   * 다른 경로(WhatsApp `BAJA`·고객센터 대행)로 이미 꺼진 동의를 이 단말의 낡은
   * 플래그가 계속 "켜짐"으로 주장하지 못하게 한다. 진행 상태(phase)는 건드리지
   * 않는다 — 폴링이 값을 다시 실어 올 때 409 안내나 오류 문구가 지워지면 안 된다.
   */
  useEffect(() => {
    if (storeId === null || serverConsentCurrent !== false) {
      return;
    }
    clearWaitlistMarketingConsent(storeId);
    setConsented(false);
  }, [storeId, serverConsentCurrent]);

  /**
   * 철회 완료 처리.
   *
   * `global` 은 "서버가 연락처 전역 철회를 수행했다고 확인된 경우"에만 참이다.
   * localStorage 는 단말 전역이지 연락처 전역이 아니라서(공유 단말에서 다른 번호로
   * 다른 매장 줄에 설 수 있다) 전수 삭제는 되돌릴 수단이 있는 쪽에서만 안전하다 —
   * 신서버는 다음 조회의 `current` 가 각 매장의 링크를 다시 정확히 그려주지만,
   * 구서버에서는 플래그가 유일한 근거라 지우는 순간 그 연락처는 이 단말에서 철회
   * 동선을 영구히 잃는다.
   */
  const settleWithdrawn = useCallback(
    (token: string, options: { global: boolean }) => {
      if (options.global) {
        clearAllWaitlistMarketingConsents();
      } else if (storeId !== null) {
        clearWaitlistMarketingConsent(storeId);
      }
      saveWaitlistMarketingWithdrawn(token);
      setConsented(false);
      setWithdrawnToken(token);
      setRecentlyWithdrawn(true);
      setPhase('idle');
    },
    [storeId],
  );

  const withdraw = useCallback(async () => {
    if (storeId === null || !accessToken) {
      return;
    }

    setPhase('pending');

    try {
      await waitlistApi.withdrawMarketingConsent(accessToken);
      // 200 을 받았고 서버가 상태를 되짚어 줄 수 있는 신서버일 때만 전역 정리.
      settleWithdrawn(accessToken, { global: serverConsentCurrent !== undefined });
    } catch (error) {
      if (isWaitlistConsentWithdrawalUnavailableError(error)) {
        // 철회되지 않았다 — 완료로 넘기지 말고 다른 연락 경로를 안내한다.
        setPhase('unavailable');
        logger.warn('[waitlist] 마케팅 동의 철회 처리 불가(409)');
        return;
      }
      if (isWaitlistConsentAbsentError(error)) {
        // "닫을 동의가 없다"는 완료와 같지만 서버가 무엇을 지웠는지는 알 수 없다 —
        // 정리 범위는 이 매장으로 좁힌다.
        settleWithdrawn(accessToken, { global: false });
        return;
      }
      // 재시도 가능한 실패 — 링크는 유지한다. 로그에 토큰/연락처는 담지 않는다.
      setPhase('failed');
      logger.warn('[waitlist] 마케팅 동의 철회 실패', error);
    }
  }, [storeId, accessToken, serverConsentCurrent, settleWithdrawn]);

  const withdrawn = (withdrawnToken !== null && withdrawnToken === accessToken) || recentlyWithdrawn;

  return {
    /**
     * 서버가 "수신 대상"이라고 했거나(신서버), 이 단말이 동의를 기록해 뒀거나(구서버
     * 폴백), 방금 철회를 마쳐 완료 문구를 보여야 할 때 렌더한다.
     *
     * 서버의 `current: false` 는 위 조정 effect 가 플래그까지 지우므로, 신서버에서
     * 합집합이 참으로 남는 경로는 "정말 수신 대상" 뿐이다. 구서버(undefined)에서는
     * 플래그가 유일한 근거이므로 그대로 신뢰한다.
     */
    visible: enabled && (withdrawn || serverConsentCurrent === true || consented),
    withdrawing: phase === 'pending',
    withdrawn,
    failed: phase === 'failed',
    unavailable: phase === 'unavailable',
    withdraw,
  };
}
